/* Shared grid and one accessible detail dialog. The STL bundle loads on demand. */
if (!customElements.get('three-d-world')) {
  customElements.define('three-d-world', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.loadStatus?.remove();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('[data-world-dialog]');
      this.content = this.querySelector('[data-world-content]');
      this.grid = this.querySelector('[data-world-grid]');
      this.search = this.querySelector('[data-world-search]');
      this.category = this.querySelector('[data-world-category]');
      this.noResults = this.querySelector('[data-world-no-results]');
      this.more = this.querySelector('[data-world-more]');
      // A native pagination URL can also be entered directly. Include earlier
      // pages when searching from that URL, not only the pages after it.
      if (Number(this.grid?.dataset.currentPage) > 1) {
        const first = new URL(location.href);
        first.searchParams.delete(this.grid.dataset.pageParam || 'page');
        first.searchParams.delete('model');
        first.hash = '';
        if (!this.more) {
          const pagination = document.createElement('div');
          pagination.className = 'world-pagination';
          this.more = document.createElement('a');
          this.more.className = 'world-button';
          this.more.dataset.worldMore = '';
          pagination.append(this.more);
          this.grid.after(pagination);
        }
        this.more.href = first.href;
        this.more.textContent = 'Load more projects';
      }
      this.cards = new Map();
      this.mediaVersion = (this.mediaVersion || 0) + 1;
      this.navigationVersion = (this.navigationVersion || 0) + 1;
      this.visitedPages = new Set();
      this.registerCards();
      if (!this.dialog?.showModal) return;
      this.querySelector('[data-world-controls]')?.removeAttribute('hidden');
      this.loadStatus = document.createElement('p');
      this.loadStatus.className = 'world-load-status';
      this.loadStatus.setAttribute('role', 'status');
      this.loadStatus.hidden = true;
      this.grid?.after(this.loadStatus);
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.search?.addEventListener('input', () => this.onFilter(), options);
      this.category?.addEventListener('change', () => this.onFilter(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.requestClose();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return; // A rapid Back/Forward may have reopened it.
        this.stopMedia();
        this.currentHandle = null;
        this.content.replaceChildren();
        if (!document.querySelector('three-d-world dialog[open]')) document.documentElement.classList.remove('world-modal-open');
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('pointerdown', (event) => { this.backdropDown = this.isBackdrop(event); }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropDown && this.isBackdrop(event)) this.requestClose();
        this.backdropDown = false;
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event), options);
      this.addEventListener('world:viewer-error', (event) => {
        this.setViewerStatus(event.detail, 'error');
        const tools = this.content.querySelector('[data-world-viewer-tools]');
        if (tools) tools.hidden = true;
      }, options);
      window.addEventListener('popstate', () => this.syncLocation(), options);
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (event) => this.selectEditorBlock(event), options);
        document.addEventListener('shopify:section:deselect', (event) => {
          if (this.editorOpen && (event.detail?.sectionId === this.dataset.sectionId || event.target.contains(this))) this.requestClose();
        }, options);
      }
      this.filterCards();
      this.syncLocation();
    }

    registerCards() {
      this.grid?.querySelectorAll('[data-world-card]').forEach((card) => {
        if (card.hasAttribute('data-world-editor-project')) this.hydrateEditorProject(card);
        this.cards.set(card.dataset.handle, card);
        const link = card.querySelector('[data-world-open]');
        link?.setAttribute('aria-haspopup', 'dialog');
        link?.setAttribute('aria-controls', this.dialog.id || (this.dialog.id = `${this.id}-dialog`));
      });
      if (!this.category) return;
      const selected = this.category.value;
      const categories = [...new Set(['Enclosures', 'Parts & mounts', 'Robots', 'Complete builds', 'Other', ...[...this.cards.values()].map((card) => card.dataset.category).filter(Boolean)])].sort();
      this.category.replaceChildren(new Option('All categories', ''));
      for (const category of categories) this.category.add(new Option(category, category));
      this.category.value = selected;
    }

    hydrateEditorProject(card) {
      const detail = card.querySelector('[data-world-detail]');
      if (!detail || detail.dataset.worldHydrated) return;
      const strip = detail.querySelector('[data-world-media-strip]');
      const photos = [...detail.querySelectorAll('[data-world-gallery-photo][href]')].filter((photo) => photo.getAttribute('href').trim());
      const cover = photos[0];
      const cardPreview = card.querySelector('[data-world-card-preview]');
      if (cover && cardPreview) {
        const image = document.createElement('img');
        image.className = 'world-card__image';
        image.alt = cover.dataset.alt || card.dataset.title || '';
        image.loading = 'lazy';
        image.width = Number(cover.dataset.cardWidth) || 900;
        image.height = Number(cover.dataset.cardHeight) || 675;
        if (cover.dataset.cardSrcset) {
          image.srcset = cover.dataset.cardSrcset;
          image.sizes = '(min-width: 990px) 30vw, (min-width: 600px) 45vw, 90vw';
        }
        image.src = cover.dataset.cardSrc || cover.href;
        cardPreview.querySelector('.world-card__placeholder')?.remove();
        cardPreview.prepend(image);
      }
      const files = [...detail.querySelectorAll('[data-world-download][href]')].filter((file) => file.getAttribute('href').trim());
      const formats = [...new Set(files.map((file) => (file.dataset.format || 'FILE').toUpperCase()))];
      const labels = files.map((file) => file.dataset.label || file.textContent.trim());
      const cardFiles = card.querySelector('[data-world-card-files]');
      if (cardFiles) cardFiles.textContent = `${files.length} file${files.length === 1 ? '' : 's'}${formats.length ? ` · ${formats.join(' · ')}` : ''}`;
      card.dataset.search = `${card.dataset.search || ''} ${formats.join(' ')} ${labels.join(' ')}`;
      const fileCount = detail.querySelector('[data-world-file-count]');
      if (fileCount) fileCount.textContent = files.length;
      const fileSection = detail.querySelector('[data-world-files-section]');
      if (fileSection) fileSection.hidden = files.length === 0;
      if (strip) {
        const stlURLs = new Set([...strip.querySelectorAll('[data-kind="stl"]')].map((button) => new URL(button.dataset.url, location.href).href));
        for (const file of files) {
          if (file.dataset.format?.toUpperCase() !== 'STL' || stlURLs.has(file.href)) continue;
          stlURLs.add(file.href);
          const label = file.dataset.label || '3D model';
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'world-media__button';
          button.dataset.worldMediaButton = '';
          button.dataset.kind = 'stl';
          button.dataset.url = file.href;
          button.dataset.label = label;
          button.dataset.worldMediaBlockId = file.closest('[data-world-block-id]')?.dataset.worldBlockId || '';
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', `View ${label} in 3D`);
          const type = document.createElement('span');
          type.className = 'world-media__type';
          type.setAttribute('aria-hidden', 'true');
          type.textContent = '3D';
          const title = document.createElement('span');
          title.className = 'world-media__label';
          title.textContent = label;
          button.append(type, title);
          strip.append(button);
        }
        for (const photo of photos) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'world-media__button';
          button.dataset.worldMediaButton = '';
          button.dataset.kind = 'photo';
          button.dataset.src = photo.href;
          button.dataset.alt = photo.dataset.alt || card.dataset.title || '';
          button.dataset.worldMediaBlockId = photo.closest('[data-world-block-id]')?.dataset.worldBlockId || '';
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', `View photo: ${button.dataset.alt || 'Project photo'}`);
          const thumbnail = photo.querySelector('img')?.cloneNode(true);
          if (thumbnail) {
            thumbnail.className = 'world-media__thumb';
            thumbnail.alt = '';
            button.append(thumbnail);
          } else button.textContent = 'Photo';
          // Photos follow the STL previews in their saved block order.
          strip.append(button);
        }
        strip.hidden = !strip.querySelector('[data-world-media-button]');
        detail.querySelectorAll('[data-world-gallery]').forEach((gallery) => { gallery.hidden = true; });
      }
      const hint = detail.querySelector('[data-world-preview-hint]');
      if (hint) hint.hidden = !strip?.querySelector('[data-kind="stl"]');
      detail.dataset.worldHydrated = 'true';
    }

    selectEditorBlock(event) {
      const blockId = String(event.detail?.blockId || '');
      if (!blockId) return;
      let card;
      let selected;
      for (const project of this.cards.values()) {
        if (!project.hasAttribute('data-world-editor-project')) continue;
        selected = [project, ...project.querySelectorAll('[data-world-block-id]')].find((block) => block.dataset.worldBlockId === blockId);
        if (selected) { card = project; break; }
      }
      if (!card) return;
      this.navigationVersion++;
      if (card.hidden) {
        if (this.search) this.search.value = '';
        if (this.category) this.category.value = '';
        this.filterCards();
      }
      if (selected === card) {
        if (this.dialog.open) {
          this.editorOpen = true;
          this.requestClose();
        }
        card.scrollIntoView({ block: 'center' });
        return;
      }
      this.openProject(card.dataset.handle, { editor: true });
      const previews = [...this.content.querySelectorAll('[data-world-media-button]')];
      let preview = previews.find((button) => button.dataset.worldMediaBlockId === blockId);
      const file = selected.matches('[data-world-download]') ? selected : selected.querySelector('[data-world-download]');
      if (!preview && file?.dataset.format?.toUpperCase() === 'STL') {
        preview = previews.find((button) => button.dataset.kind === 'stl' && new URL(button.dataset.url, location.href).href === file.href);
      }
      if (preview) {
        this.showMedia(preview);
        preview.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        if (selected.matches('[data-world-file]') && !file) {
          this.showPreviewMessage('Add a file link in this File block to show its Download button.');
        } else if (selected.matches('[data-world-photo-block]') && !selected.querySelector('[data-world-gallery-photo]')) {
          this.showPreviewMessage('Choose an image in this Photo block to add it to the gallery.');
        }
        const selectedClone = [...this.content.querySelectorAll('[data-world-block-id]')].find((block) => block.dataset.worldBlockId === blockId);
        selectedClone?.scrollIntoView({ block: 'nearest' });
      }
    }

    onClick(event) {
      const opener = event.target.closest('[data-world-open]');
      if (opener && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        this.navigationVersion++;
        const editor = Boolean(window.Shopify?.designMode);
        this.openProject(opener.dataset.handle, { push: !editor, trigger: opener, editor });
      } else if (event.target.closest('[data-world-close]')) {
        this.requestClose();
      } else if (event.target.closest('[data-world-share]')) {
        this.share();
      } else if (event.target.closest('[data-world-media-button]')) {
        this.showMedia(event.target.closest('[data-world-media-button]'));
      } else if (event.target.closest('[data-world-reset]')) {
        this.viewer?.reset();
      } else if (event.target.closest('[data-world-zoom-in]')) {
        this.viewer?.zoomIn();
      } else if (event.target.closest('[data-world-zoom-out]')) {
        this.viewer?.zoomOut();
      } else if (event.target.closest('[data-world-more]')) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.loadMore().then(() => {
          this.onFilter();
          if (!this.dialog.open) this.syncLocation();
        }).catch(() => {});
      }
    }

    filterCards() {
      const words = (this.search?.value || '').trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      const category = this.category?.value || '';
      let visible = 0;
      for (const card of this.cards.values()) {
        const text = card.dataset.search.toLocaleLowerCase();
        card.hidden = !words.every((word) => text.includes(word)) || Boolean(category && card.dataset.category !== category);
        if (!card.hidden) visible++;
      }
      if (this.noResults) this.noResults.hidden = visible > 0 || Boolean(this.loadingAll);
    }

    async onFilter() {
      this.filterCards();
      // Finish loading the catalog when filtering, so search never silently
      // excludes matching entries that happen to be on another Shopify page.
      if (!this.more || this.loadingAll || (!this.search.value.trim() && !this.category.value)) return;
      this.loadingAll = true;
      this.filterCards();
      try {
        while (this.more && this.isConnected) await this.loadMore();
      } catch { /* Keep already-loaded results and a visible retry link. */ }
      finally {
        this.loadingAll = false;
        this.filterCards();
      }
    }

    async loadMore() {
      if (this.pagePromise) return this.pagePromise;
      if (!this.more) return;
      this.pagePromise = this.fetchNextPage();
      try { await this.pagePromise; }
      finally { this.pagePromise = null; }
    }

    async fetchNextPage() {
      const link = this.more;
      const url = new URL(link.href, location.origin);
      url.hash = '';
      url.searchParams.delete('model');
      url.searchParams.set('section_id', this.dataset.sectionId);
      if (url.origin !== location.origin || this.visitedPages.has(url.href)) {
        throw new Error('Invalid catalog page');
      }
      link.setAttribute('aria-disabled', 'true');
      this.loadStatus.hidden = false;
      this.loadStatus.textContent = 'Loading more projects…';
      const request = new AbortController();
      this.pageRequest = request;
      const timeout = setTimeout(() => request.abort(), 15000);
      try {
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Catalog unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextSection = html.getElementById(this.id);
        if (!nextSection?.querySelector('[data-world-grid]')) throw new Error('Missing catalog');
        for (const card of nextSection.querySelectorAll('[data-world-card]')) {
          if (!this.cards.has(card.dataset.handle)) this.grid.append(document.importNode(card, true));
        }
        this.visitedPages.add(url.href);
        const nextLink = nextSection.querySelector('[data-world-more]');
        if (nextLink) link.href = nextLink.href;
        else {
          link.parentElement.hidden = true;
          this.more = null;
        }
        this.registerCards();
        this.filterCards();
        this.loadStatus.hidden = true;
      } catch (error) {
        if (this.isConnected) {
          this.loadStatus.textContent = 'Some projects could not be loaded. Select “Load more projects” to retry.';
          this.loadStatus.hidden = false;
        }
        throw error;
      } finally {
        clearTimeout(timeout);
        link.removeAttribute('aria-disabled');
        if (this.pageRequest === request) this.pageRequest = null;
      }
    }

    async syncLocation() {
      const version = ++this.navigationVersion;
      const handle = new URL(location.href).searchParams.get('model');
      if (!handle) {
        if (this.dialog.open) this.dialog.close();
        return;
      }
      try {
        while (!this.cards.has(handle) && this.more && version === this.navigationVersion) await this.loadMore();
        if (version !== this.navigationVersion || !this.isConnected) return;
        if (this.cards.has(handle)) this.openProject(handle);
        else if (this.loadStatus) {
          this.loadStatus.textContent = 'This project is no longer available. Explore the other projects below.';
          this.loadStatus.hidden = false;
        }
      } catch { /* The load status provides an actionable failure. */ }
    }

    openProject(handle, { push = false, trigger, editor = false } = {}) {
      const card = this.cards.get(handle);
      const template = card?.querySelector('[data-world-detail]');
      if (!template) return;
      this.editorOpen = editor;
      if (this.currentHandle === handle && this.dialog.open) return;
      this.stopMedia();
      this.returnFocus = trigger || card.querySelector('[data-world-open]');
      this.currentHandle = handle;
      const source = template.content || template;
      this.content.replaceChildren(...[...source.childNodes].map((node) => node.cloneNode(true)));
      this.content.querySelectorAll('[data-shopify-editor-block]').forEach((block) => block.removeAttribute('data-shopify-editor-block'));
      this.content.querySelector('[data-world-title]').id = this.dialog.getAttribute('aria-labelledby');
      this.querySelector('[data-world-share-status]').replaceChildren();
      if (push) {
        const url = new URL(location.href);
        url.searchParams.set('model', handle);
        history.pushState({ ...history.state, worldDialog: this.id }, '', url);
      }
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('world-modal-open');
      this.dialog.scrollTop = 0;
      this.content.scrollTop = 0;
      this.querySelector('[data-world-close]').focus({ preventScroll: true });
      const firstMedia = this.content.querySelector('[data-world-media-button][data-kind="stl"]')
        || this.content.querySelector('[data-world-media-button]');
      if (firstMedia) this.showMedia(firstMedia);
      else this.showPreviewMessage(window.Shopify?.designMode
        ? 'Add a Photo block or an STL File block to preview this project.'
        : this.content.querySelector('[data-world-download]')
          ? 'No preview available. Explore the downloadable files below.'
          : 'No preview available yet.');
    }

    requestClose() {
      if (!this.dialog.open) return;
      this.navigationVersion++;
      const editorOpen = this.editorOpen;
      this.editorOpen = false;
      this.dialog.close();
      if (editorOpen) return;
      if (history.state?.worldDialog === this.id) history.back();
      else {
        const url = new URL(location.href);
        url.searchParams.delete('model');
        history.replaceState(history.state, '', url);
      }
    }

    stopMedia() {
      this.mediaVersion++;
      this.mediaRequest?.abort();
      this.mediaRequest = null;
      clearTimeout(this.mediaTimeout);
      this.viewer?.dispose();
      this.viewer = null;
      const photo = this.content?.querySelector('[data-world-photo]');
      if (photo) {
        photo.onload = null;
        photo.onerror = null;
        photo.removeAttribute('src');
        photo.hidden = true;
      }
    }

    setViewerStatus(text, state) {
      const status = this.content.querySelector('[data-world-viewer-status]');
      if (status) {
        status.textContent = text;
        status.hidden = !text;
      }
      const media = this.content.querySelector('[data-world-media]');
      if (media) media.dataset.state = state;
    }

    showPreviewMessage(message) {
      this.stopMedia();
      this.content.querySelectorAll('[data-world-viewer], [data-world-viewer-tools], [data-world-preview-hint], .world-media__hint').forEach((element) => { element.hidden = true; });
      this.content.querySelectorAll('[data-world-media-button]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      this.setViewerStatus(message, 'empty');
    }

    safeMediaURL(value) {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('This preview link is unavailable.');
      return url.href;
    }

    async showMedia(button) {
      this.stopMedia();
      const version = this.mediaVersion;
      const stage = this.content.querySelector('[data-world-viewer]');
      const tools = this.content.querySelector('[data-world-viewer-tools]');
      const photo = this.content.querySelector('[data-world-photo]');
      this.content.querySelectorAll('[data-world-media-button]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      tools.hidden = true;
      stage.hidden = button.dataset.kind !== 'stl';
      const hint = this.content.querySelector('[data-world-preview-hint], .world-media__hint');
      if (hint) hint.hidden = button.dataset.kind !== 'stl';
      this.setViewerStatus(button.dataset.kind === 'stl' ? 'Loading 3D preview…' : 'Loading photo…', 'loading');
      if (button.dataset.kind === 'photo') {
        try {
          photo.alt = button.dataset.alt || '';
          photo.onload = () => {
            if (version !== this.mediaVersion) return;
            photo.hidden = false;
            this.setViewerStatus('', 'photo');
          };
          photo.onerror = () => {
            if (version === this.mediaVersion) this.setViewerStatus('This photo could not be loaded. Try another preview.', 'error');
          };
          photo.src = this.safeMediaURL(button.dataset.src);
        } catch (error) { this.setViewerStatus(error.message, 'error'); }
        return;
      }
      const request = new AbortController();
      this.mediaRequest = request;
      let timedOut = false;
      this.mediaTimeout = setTimeout(() => {
        timedOut = true;
        request.abort();
        if (version === this.mediaVersion && this.dialog.open) {
          this.setViewerStatus('The preview took too long to load. Select the part to retry, or use its download link.', 'error');
        }
      }, 20000);
      try {
        if (!this.viewerModule) {
          this.viewerModule = import(this.dataset.viewerUrl).catch((error) => {
            this.viewerModule = null;
            throw error;
          });
        }
        const module = await this.viewerModule;
        if (version !== this.mediaVersion || !this.dialog.open) return;
        const geometry = await module.loadSTL(this.safeMediaURL(button.dataset.url), request.signal);
        if (version !== this.mediaVersion || !this.dialog.open) {
          geometry.dispose();
          return;
        }
        this.viewer = module.createSTLViewer(stage, geometry, button.dataset.label);
        tools.hidden = false;
        this.setViewerStatus('', 'ready');
      } catch (error) {
        if (version !== this.mediaVersion || !this.dialog.open) return;
        const message = timedOut ? 'The preview took too long to load. Select the part to retry, or use its download link.'
          : error instanceof TypeError ? 'The 3D preview could not be loaded. Photos and download links are still available.'
            : error.message || 'The 3D preview is unavailable. You can still download this file.';
        this.setViewerStatus(message, 'error');
      } finally {
        if (version === this.mediaVersion) clearTimeout(this.mediaTimeout);
      }
    }

    async share() {
      const status = this.querySelector('[data-world-share-status]');
      const handle = this.currentHandle;
      const url = new URL(location.href);
      url.searchParams.set('model', handle);
      // Preserve the theme-preview parameters, but use the first catalog page
      // so a shared model stays discoverable when later additions reorder it.
      for (const key of [...url.searchParams.keys()]) {
        if (key === 'page' || /^page_/.test(key)) url.searchParams.delete(key);
      }
      url.hash = '';
      try {
        await navigator.clipboard.writeText(url.href);
        if (this.currentHandle === handle) status.textContent = 'Link copied';
      } catch {
        if (this.currentHandle !== handle) return;
        const input = document.createElement('input');
        input.type = 'text';
        input.readOnly = true;
        input.value = url.href;
        input.setAttribute('aria-label', 'Project link — copy this address');
        status.replaceChildren(input);
        input.focus();
        input.select();
      }
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const elements = [...this.dialog.querySelectorAll('a[href],button:not(:disabled),input,select,textarea,[tabindex="0"]')].filter((item) => item.getClientRects().length);
      const first = elements[0];
      const last = elements.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    disconnectedCallback() {
      this.navigationVersion++;
      this.stopMedia();
      this.pageRequest?.abort();
      this.events?.abort();
      this.loadStatus?.remove();
      if (this.dialog?.open) this.dialog.close();
      this.content?.replaceChildren();
      this.currentHandle = null;
      this.editorOpen = false;
      if (!document.querySelector('three-d-world dialog[open]')) document.documentElement.classList.remove('world-modal-open');
    }
  });
}
